var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Col, Row } from "@folio/stripes/components";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { useInlineReportMutation } from "../../../hooks/useInlineReportMutation";
import css from "./reports.module.css";
import { createIpaReportSchema } from "../../../utils/validationUtils";
import { generateIpaReport } from "../../../api/reportsService";
import { IpaReportParamsRegistrationAgencyEnum, IpaReportDataRegistrationAgencyEnum, } from "../../../types/Api";
import { RecekTextField } from "../../inputs/RecekTextField";
import { RegistrationAgencySelect } from "./RegistrationAgencySelect";
import { ReportSubmitSection } from "./ReportSubmitSection";
var getDefaultValues = function () { return ({
    year: String(new Date().getFullYear() - 1),
    registrationAgency: IpaReportParamsRegistrationAgencyEnum.ISBN,
}); };
var transformFormToApiParams = function (data) { return ({
    year: parseInt(data.year, 10),
    registrationAgency: data.registrationAgency,
}); };
/**
 * Returns a translated label for the given registration agency enum value.
 */
var useAgencyLabel = function () {
    var formatMessage = useIntl().formatMessage;
    return function (agency) {
        var key = agency === IpaReportDataRegistrationAgencyEnum.ISBN
            ? "ui-br-agency.outputs.common.registrationAgency.isbn"
            : "ui-br-agency.outputs.common.registrationAgency.ismn";
        return formatMessage({ id: key });
    };
};
export var IpaReportForm = function () {
    var _a = useIntl(), formatMessage = _a.formatMessage, locale = _a.locale;
    var agencyLabel = useAgencyLabel();
    var _b = useForm({
        resolver: zodResolver(createIpaReportSchema(formatMessage)),
        defaultValues: getDefaultValues(),
    }), control = _b.control, handleSubmit = _b.handleSubmit;
    var _c = useInlineReportMutation({
        generateReport: function (reportKy, data) {
            return generateIpaReport(reportKy, transformFormToApiParams(data));
        },
        successMessageId: "ui-br-agency.outputs.ipa-report.success.generate",
        errorMessageId: "ui-br-agency.outputs.ipa-report.error.generate",
    }), mutate = _c.mutate, isLoading = _c.isLoading, reportData = _c.data, successMessage = _c.successMessage, clearSuccessMessage = _c.clearSuccessMessage, errorModalOpen = _c.errorModalOpen, errorMessage = _c.errorMessage, closeErrorModal = _c.closeErrorModal;
    var formatCount = function (count) { return count.toLocaleString(locale); };
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { onSubmit: handleSubmit(function (data) { return mutate(data); }) },
            React.createElement("h2", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.ipa-report" })),
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.ipa-report.description" })),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 4 },
                    React.createElement(RegistrationAgencySelect, { control: control, name: "registrationAgency" })),
                React.createElement(Col, { xs: 12, md: 4 },
                    React.createElement(Controller, { control: control, name: "year", render: function (_a) {
                            var _b;
                            var field = _a.field, fieldState = _a.fieldState;
                            return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.outputs.ipa-report.field.year", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, required: true })));
                        } }))),
            React.createElement(ReportSubmitSection, { isLoading: isLoading, generateButtonId: "ui-br-agency.outputs.ipa-report.button.generate", successMessage: successMessage, onSuccessDismiss: clearSuccessMessage, errorModalOpen: errorModalOpen, errorMessage: errorMessage, onErrorClose: closeErrorModal })),
        reportData &&
            (reportData.activePublisherCount === 0 ? (React.createElement("div", { className: css.ipaResult },
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.ipa-report.result.empty" }))) : (React.createElement("div", { className: css.ipaResult },
                React.createElement("h3", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.ipa-report.result.title", values: {
                            agency: agencyLabel(reportData.registrationAgency),
                        } })),
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.ipa-report.result.activePublisherCount", values: {
                            agency: agencyLabel(reportData.registrationAgency),
                            year: reportData.year,
                            count: formatCount(reportData.activePublisherCount),
                            b: function (chunks) { return React.createElement("b", null, chunks); },
                        } })))))));
};
